/**
 * OmegaDiscoveryLabPage — per-lab discovery output detail page.
 *
 * Route: /omega/brain/discovery/:labName/outputs
 *
 * Fetches outputs for the given lab from GET
 * /api/v1/omega/brain/discovery/{labName}/outputs?limit=20.
 * Supports pin and archive mutations per row.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';
import { cn } from '@/utils/cn';

interface DiscoveryOutputRecord {
  id: string;
  lab_name: string;
  output_type: string;
  payload: unknown;
  cycle_id: string;
  created_iso: string;
  summary: string;
  pinned: boolean;
  archived: boolean;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function OutputTypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-surface-raised px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
      {type}
    </span>
  );
}

function OutputCard({
  output,
  onPin,
  onArchive,
}: {
  output: DiscoveryOutputRecord;
  onPin: (id: string) => void;
  onArchive: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const payloadStr = JSON.stringify(output.payload, null, 2);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-canvas p-4',
        output.pinned ? 'border-spark/40' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-subtle">{formatTimestamp(output.created_iso)}</span>
        <OutputTypeBadge type={output.output_type} />
        {output.pinned && (
          <span className="inline-flex items-center rounded-full border border-spark/40 bg-spark/10 px-2 py-0.5 text-[10px] font-medium text-spark">
            Pinned
          </span>
        )}
      </div>

      {output.summary && (
        <p className="font-semibold text-sm text-ink">{output.summary}</p>
      )}

      <details
        open={expanded}
        onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
        className="text-sm"
      >
        <summary className="cursor-pointer select-none text-[11px] text-ink-muted hover:text-ink">
          {expanded ? 'Hide payload' : 'Show payload'}
        </summary>
        <pre className="ltr-content mt-2 max-h-64 overflow-auto rounded-md border border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted whitespace-pre-wrap break-all">
          {payloadStr}
        </pre>
      </details>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onPin(output.id)}
          className={cn(
            'rounded border px-2 py-1 text-xs transition-colors',
            output.pinned
              ? 'border-spark/30 text-spark hover:border-spark'
              : 'border-border text-ink-muted hover:border-spark hover:text-spark',
          )}
        >
          {output.pinned ? 'Unpin' : 'Pin'}
        </button>
        <button
          type="button"
          onClick={() => onArchive(output.id)}
          className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:border-danger hover:text-danger transition-colors"
        >
          Archive
        </button>
      </div>
    </div>
  );
}

export function OmegaDiscoveryLabPage() {
  const { labName } = useParams<{ labName: string }>();
  const qc = useQueryClient();

  const { data: outputs = [], isPending, isError } = useQuery({
    queryKey: ['omega', 'discovery', 'lab', labName],
    queryFn: () =>
      apiFetch<DiscoveryOutputRecord[]>(
        `/omega/brain/discovery/${encodeURIComponent(labName ?? '')}/outputs?limit=20`,
      ),
    refetchInterval: 30_000,
    staleTime: 20_000,
    enabled: !!labName,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['omega', 'discovery', 'lab', labName] });

  const pinMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/omega/brain/discovery/outputs/${id}/pin`, {
        method: 'POST',
      }),
    onSuccess: invalidate,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/omega/brain/discovery/outputs/${id}/archive`, {
        method: 'POST',
      }),
    onSuccess: invalidate,
  });

  // S46: per-lab "Run Now" — triggers a discovery cycle and returns this lab's
  // fresh output count, then refetches the outputs list.
  const runMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ lab: string; output_count: number; ran_at_iso: string }>(
        `/omega/brain/discovery/${encodeURIComponent(labName ?? '')}/run`,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  });

  const displayName = labName
    ? labName
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : '';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <Link
          to="/omega/brain/discovery"
          className="text-sm text-ink-muted hover:text-ink hover:underline"
        >
          ← Discovery Brain
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{displayName}</h1>
          <p className="text-sm text-ink-muted">Recent lab outputs — up to 20, newest first</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
              runMutation.isPending
                ? 'border-border text-ink-muted'
                : 'border-spark/40 text-spark hover:border-spark hover:bg-spark/10',
            )}
          >
            {runMutation.isPending ? 'Running…' : 'Run Now'}
          </button>
          {runMutation.isSuccess && runMutation.data && (
            <span className="text-[11px] text-ink-muted">
              Ran — {runMutation.data.output_count} output
              {runMutation.data.output_count === 1 ? '' : 's'} this lab.
            </span>
          )}
          {runMutation.isError && (
            <span className="text-[11px] text-danger">Run failed — try again.</span>
          )}
        </div>
      </header>

      {isPending && (
        <p className="text-sm text-ink-muted">Loading outputs…</p>
      )}

      {isError && (
        <p className="text-sm text-danger">Failed to load outputs for this lab.</p>
      )}

      {!isPending && !isError && outputs.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-surface p-8 text-center">
          <p className="text-sm text-ink-muted">No outputs recorded for {displayName} yet.</p>
        </div>
      )}

      {outputs.length > 0 && (
        <div className="flex flex-col gap-3">
          {outputs.map((output) => (
            <OutputCard
              key={output.id}
              output={output}
              onPin={(id) => pinMutation.mutate(id)}
              onArchive={(id) => archiveMutation.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default OmegaDiscoveryLabPage;
